import cn from 'clsx'
import { replace } from 'connected-react-router'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import Pane from 'components/Common/Pane'
import OfferPageGallery from '../OfferPageGallery'
import FlashHotelDesktopSameRoomOptions from './FlashHotelDesktopSameRoomOptions'

import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { clearBookingCalendar, fetchCalendars, fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { fetchOfferFlightPrice, fetchRelatedTravelItems } from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'

import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { urlHashDatePicker } from 'constants/url'
import { OfferPageActions, OfferPageFilterType, OfferPageMultiRoomMode, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import { nonNullable } from 'lib/array/arrayUtils'
import { isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import FlashHotelDesktopIndividualRooms from './FlashHotelDesktopIndividualRooms'
import OfferIntro from './FlashHotelIntro'
import FlashHotelSidebarFilters from './FlashHotelSidebarFilters'
import FlashHotelHeader from './FlashHotelStickyHeader'
import HotelOfferPageStickyFooter from '../HotelOfferPageStickyFooter'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusNotificationBanner from 'luxPlus/components/Banners/LuxPlusNotificationBanner'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import AccommodationOfferPageStickyFooter from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyFooter'
import SavePromptHotel from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptHotel'
import { encodeSearchParams } from 'lib/search/searchUtils'
import useShareModalConfig from 'hooks/useShareModalConfig'
import WalledContent from 'components/Common/WalledContent'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import { getCanUseMultiRoomUI, getCheapestSelectedPackageOptionWithDefault, getCheckoutDate, isAvailableForPurchase, offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import { useLocation } from 'react-router'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import FlashMobileStickyHeaderFilters from './FlashHotelMobileVariant/FlashMobileStickyHeaderFilters'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { trackDurationMs } from 'analytics/perf'
import HotelDesktopFlightToggle from '../Accommodation/HotelDesktopFlightToggle'
import MultiRoomModeToggle from '../MultipleRoomsBooking/MultiRoomModeToggle'
import OfferDesktopConnectingRoomsToggle from '../Filters/ConnectingRooms/OfferDesktopConnectingRoomsToggle'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'
import useHotelIndividualRoomAutoselection from 'hooks/OfferPage/useHotelIndividualRoomAutoselection'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import FlashHotelMobileRoomOptions from './FlashHotelMobileVariant/FlashHotelMobileRoomOptions'
import useHasMounted from 'hooks/useHasMounted'
import { CHARTER_BUNDLE_DURATION_OPTIONS } from 'constants/charterFlight'
import ReturnCharterFlightTile from 'components/Flights/CharterBundle/ReturnCharterFlightTile'
import CharterFlightOfferAirportSelect from '../Filters/CharterFlight/CharterFlightOfferAirportSelect'
import CharterFlightFareClassSelect from '../Filters/CharterFlight/CharterFlightFareClassSelect'
import CharterFlightCalendarSelect from '../Filters/CharterFlight/CharterFlightCalendarSelect'
import { FlightsClassTypes } from 'constants/flight'
import Caption from 'LuxKit/Typography/Caption'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import PriceModeSelectDesktop from 'components/OfferPage/Filters/PriceModeSelect/PriceModeSelectDesktop'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'

const RelativeBlock = styled.div`
  position: relative;
`

const StyledFlashHotelStickyHeader = styled(FlashHotelHeader)`
  position: relative;

  &.hideOnMobile.hideOnMobile {
    display: none;

    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const PackageOptionsPane = styled(Pane)<{ $tightMobileTopPadding?: boolean }>`
  padding: ${rem(20)} 0;

  ${mediaQueryDown.mobile} {
    background: ${({ theme }) => theme.palette.neutral.default.one};

    ${props => props.$tightMobileTopPadding && css`
      padding-top: 0;
    `}
  }
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledOfferCapacityWithAgesSelect = styled(OfferCapacityWithAgesSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferCalendarDatePickerSelect = styled(OfferCalendarDatePickerSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;

  &:has(> :nth-child(3)) > * {
    flex: 1;
    max-width: unset;
  }

  ${mediaQueryDown.desktop} {
    &:has(> :nth-child(4)) > * {
      /* To ensure when they wrap we don't get three filters on the first row and then on on the second row */
      min-width: 34%;
    }
  }
`

interface Props {
  offer: App.HotelOffer
  airport?: App.Airport
  duration: number
  activeFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  availableForPurchase?: boolean
  bestPackage: App.OfferPackageOptionView
  room?: App.PackageRoomType
  flightsEnabled?: boolean
  isFlightsSelected: boolean
  roomMode: OfferPageMultiRoomMode
  multipleRoomBookingActive: boolean
  canUseMultiRoomUI: boolean
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FlightsClassTypes
  priceMode: OfferPagePriceMode
}

/**
 * Until there is a formal way to set a video to auto play on the offer itself
 * We'll see a magic list of offer ids for now
 */
const magicAutoPlayVideoOfferIds = new Set(['006RF00000CoK6HYAV', '006RF00000DX4JtYAL'])

const OFFER_GALLERY_TOP_ROOT_ID = 'offer-gallery-top-root'

function FlashHotelContent(props: Props) {
  const {
    offer,
    airport,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    availableForPurchase,
    bestPackage,
    room,
    activeHeaderFilter,
    flightsEnabled,
    isFlightsSelected,
    roomMode,
    multipleRoomBookingActive,
    canUseMultiRoomUI,
    charterBundleRoute,
    fareClass,
    priceMode,
  } = props

  const location = useLocation()
  const { originAirports, airports } = useContext(GeoContext)
  const luxPlusEnabled = useAppSelector(state => getIsLuxPlusEnabled(state))
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)
  const isMobileDevice = useScreenSizeOnly('mobile')

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(!!checkInDate)

  const { pricing, pricingWithFlights } = bestPackage
  const bundledWithFlightsOnly = offer.bundledWithFlightsOnly || !!charterBundleRoute
  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showDiscountPill = !offer?.isDiscountPillHidden && (
    (shouldShowDiscountPercent && !bundledWithFlightsOnly) ||
    (bundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  )
  const loggedOutSavedPercent = showDiscountPill
    ? bundledWithFlightsOnly
      ? pricingWithFlights?.discountPercent
      : Math.max(pricing.discountPercent, pricingWithFlights?.discountPercent ?? 0)
    : undefined

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const topImageLoadSentRef = useRef<boolean>(false)
  const topImageStartMsRef = useRef<number>(0)
  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.RoomOccupants> | null>(null)
  const lastFetchedAirport = useRef<App.Airport | null>(null)
  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)
  const [stickyFooterVisible, setIsStickyFooterVisible] = useState<boolean>(true)
  // Charter calendar UI state
  const [selectedMonth, setSelectedMonth] = useState<string>(
    checkInDate ? moment(checkInDate).format('MMM') : moment().format('MMM')
  )

  // Get dateRangesByMonth from charter flight journeys API
  // Use fixed date range to fetch all available flights, then extract all dates from the response
  const { dateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms,
    fareClass,
    disabled: !charterBundleRoute || rooms.length === 0,
  })

  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()

  useEffect(() => {
    const months = Object.keys(dateRangesByMonth)
    if (months.length > 0 && !months.some(month => month.toLowerCase() === selectedMonth.toLowerCase())) {
      setSelectedMonth(months[0])
    }
  }, [dateRangesByMonth, selectedMonth])

  // Filter durationOptions to only include those with matching packages
  const filteredDurationOptions = useMemo(() => {
    return CHARTER_BUNDLE_DURATION_OPTIONS.filter(option => {
      // Check if offer has a package with matching duration
      return offer.packages.some(pkg => pkg.duration === option.value)
    })
  }, [offer.packages])

  // Auto-switch to first available duration if current duration has no matching package
  // Check when component mounts or when offer packages change
  useEffect(() => {
    if (!charterBundleRoute) return
    if (filteredDurationOptions.length === 0) return

    const hasMatchingPackage = offer.packages.some(pkg => pkg.duration === duration)
    if (!hasMatchingPackage) {
      const firstAvailableDuration = filteredDurationOptions[0].value
      // Only switch if different from current duration to avoid unnecessary updates
      if (firstAvailableDuration !== duration) {
        if (checkInDate) {
          const newCheckOut = moment(checkInDate).add(firstAvailableDuration, 'days').format(ISO_DATE_FORMAT)
          stateDispatch({ type: OfferPageActions.setDates, checkIn: checkInDate, checkOut: newCheckOut })
        } else {
          stateDispatch({ type: OfferPageActions.setDuration, duration: firstAvailableDuration })
        }
      }
    }
  }, [charterBundleRoute, duration, offer.packages, filteredDurationOptions, checkInDate, stateDispatch])

  const handleCharterDurationChange = useCallback((newNights: number) => {
    if (!checkInDate) return
    const newCheckOut = moment(checkInDate).add(newNights, 'days').format(ISO_DATE_FORMAT)
    stateDispatch({ type: OfferPageActions.setDates, checkIn: checkInDate, checkOut: newCheckOut })
  }, [stateDispatch, checkInDate])

  // useDefaultMultipleRoomPackages()

  const onStickyFooterToggle = useCallback((visible: boolean) => {
    setIsStickyFooterVisible(visible)
  }, [])

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()
  useIdleTimeReporter(IdleTimeSource.FlashHotel)

  const queryString = useMemo(() => {
    return encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
      multiRoomMode: roomMode,
      fareClass,
      priceMode,
    }).toString()
  }, [checkInDate, checkOutDate, rooms, roomMode, fareClass, location.search, priceMode])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
  }, [dispatch, offer, checkInDate, rooms, checkOutDate])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => {
    requestAnimationFrame(() => {
      packageOptionsAnchor.current?.scrollIntoView({ behavior: 'auto' })
    })
  }, [])

  useEffect(() => {
    topImageStartMsRef.current = performance.now()
    topImageLoadSentRef.current = false
    return
  }, [offer.id])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const fetchCalendarData = useCallback(() => {
    if (!offer.packages.length || !isValidOccupancy(...rooms)) {
      // if we have any invalid ages or no packages, don't try fetch - it'll fail anyway
      return
    }

    dispatch(fetchCalendarsByOccupancy(offer.id, {
      occupants: rooms,
      flightOrigin: airport?.code,
      packages: offer.packages.filter(p => p.duration === duration),
    }))

    if (isMobileDevice || !multipleRoomBookingActive) {
      // 'new mobile hotel offer page' doesn't support per room multi room booking in the modals
      // So we still have to fetch the combined calendar in these circumstances as well
      // the main page will still use the existing multi room if they wish to do it there
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: nonNullable([offer.lowestPricePackage]),
      }))
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    }
    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport ?? null
  }, [airport, multipleRoomBookingActive, dispatch, duration, offer, rooms, isMobileDevice])

  useEffect(() => {
    if (charterBundleRoute) {
      fetchCalendarData()
    }
  }, [charterBundleRoute, fetchCalendarData])

  useEffect(() => {
    const hasRooms = hasSelectedRooms(rooms)
    const hasDate = !!checkInDate

    if (previousFilter === 'capacity' &&
      !hasRooms &&
      !activeFilter
    ) {
      autoScrollToFilters()
      scrollAfterFilterClose.current = false
    }

    if (activeFilter) {
      autoScrollToFilters()
    }

    if (previousFilter && !activeFilter && hasRooms && hasDate) {
      if (scrollAfterFilterClose.current) {
        if (room) {
          window.location.hash = room.id
        } else {
          autoScrollToFilters()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if ((activeFilter === 'checkin' || activeHeaderFilter === 'checkin') && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else {
      const airportChanged = lastFetchedAirport.current !== airport
      const roomsChanged = lastFetchedRooms.current !== rooms
      const requiresNewCalendar = multipleRoomBookingActive ? airportChanged || roomsChanged : viewedDates && (airportChanged || roomsChanged)
      if (requiresNewCalendar) {
        // rooms/airport has changed, all loaded calendars are no longer valid as we load calendars for a specific
        // combinations of flight origin (flight prices) and rooms (availability), so clear them

        // if multiple room booking is available and we changed the rooms,
        // don't clear the previous data, just update for the new changed
        // rooms (is filtered inside fetchCalendarsByOccupancy action)
        if (!multipleRoomBookingActive || isMobileDevice) {
          dispatch(clearBookingCalendar())
        }
        fetchCalendarData()
      }
    }
  }, [activeFilter, activeHeaderFilter, fetchCalendarData]) // eslint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages, multipleRoomBookingActive])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    if (airport) {
      // effectively an 'on airport change' event
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(offer.id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker && !charterBundleRoute) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate?.capacities ?? [])
  }, [offer])

  const isValidRoomOccupancy = offer.bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = !!flightsEnabled && isFlightsSelected && !!destinationAirport && (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) && isValidRoomOccupancy && !!flightPrice

  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  const shareModalConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ') ?? '',
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  const isLuxPlusMonthTakeoverEnabled = useCurrentSiteTakeoverV2({ offer })?.id === LUX_PLUS_MONTH_TAKEOVER_ID

  useHotelIndividualRoomAutoselection()

  const hasMounted = useHasMounted()
  const showNewMobileMultipleRoom = isMobileDevice && hasMounted

  return (
    <ProductPaletteProvider product={offer}>
      <ImmersiveSaveButtonPortal>
        <BookmarkButton offer={offer} />
      </ImmersiveSaveButtonPortal>
      <TripGuard>
        <SavePromptHotel offer={offer} />
      </TripGuard>

      <RelativeBlock onSubmit={(e) => e.preventDefault()}>
        <AccommodationOfferPageStickyHeaderMobile>
          <FlashMobileStickyHeaderFilters
            onCharterDurationChange={handleCharterDurationChange}
          />
        </AccommodationOfferPageStickyHeaderMobile>
        <CSSBreakpoint min="tablet">
          <StyledFlashHotelStickyHeader
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            className={cn({ hideOnMobile: hideSoldOutOnMobile })}
          >
            {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
          </StyledFlashHotelStickyHeader>
        </CSSBreakpoint>
        {luxPlusEnabled && !!offer.luxPlus?.access && !isLuxPlusMonthTakeoverEnabled &&
          <LuxPlusNotificationBanner>
            {offer.luxPlus.access === 'earlyAccess' && 'Members get early access to this offer before the general public and more'}
            {offer.luxPlus.access === 'memberOnly' && 'You get exclusive access to this member-only offer'}
          </LuxPlusNotificationBanner>}
        {availableForPurchase && <WalledContent enforced={offer.walledGarden}>
          <Breakpoint only="mobile">
            <FlashHotelSidebarFilters
              capacities={occupancyLimits}
            />
          </Breakpoint>
        </WalledContent>}
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations hideOffers={!!activeFilter} />
          </div>
        )}
        <div id={OFFER_GALLERY_TOP_ROOT_ID}>
          <OfferPageGallery
            offer={offer}
            shareModalConfig={shareModalConfig}
            autoPlayVideo={magicAutoPlayVideoOfferIds.has(offer.id) || !!offer.isVideoVimeoHero}
            charterBundleRoute={charterBundleRoute}
            onTopHeroImageLoad={() => {
              if (topImageLoadSentRef.current) return
              topImageLoadSentRef.current = true
              trackDurationMs('flash_offer_top_image_load_time_ms', topImageStartMsRef.current)
            }}
          />
        </div>
        <Pane>
          <OfferIntro
            onViewPackageOptions={scrollToPackageOptions}
            flightDestination={destinationAirport}
            shareModalConfig={shareModalConfig}
          />
        </Pane>
        <ScrollAnchor ref={packageOptionsAnchor} />
        <SoftOfferPageWalledGarden>
          <PackageOptionsPane
            id="Package-Options"
            $tightMobileTopPadding={isMobileDevice && !!charterBundleRoute}
          >
            <VerticalSpacer gap={32}>
              <CSSBreakpoint max="mobile">
                <FlashHotelMobileRoomOptions
                  offer={offer}
                  showFlightInformation={showFlightInformation}
                  selectedFareClass={fareClass}
                />
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <VerticalSpacer gap={32}>
                  <LayoutContainer>
                    <VerticalSpacer gap={32}>
                      {availableForPurchase && <WalledContent enforced={offer.walledGarden}>
                        <CSSBreakpoint min="tablet">
                          <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
                          <FlexDesktopFilterBlock direction="horizontal" gap={8} horizontalAlign="center" wrap="wrap" className={cn({ open: !!activeFilter })}>
                            <StyledOfferCapacityWithAgesSelect
                              boundaryRef={desktopFiltersWrapperRef}
                              capacities={occupancyLimits}
                              disableMultiRoom={isFlightForceBundled(offer)}
                            />
                            {charterBundleRoute && <>
                              <CharterFlightOfferAirportSelect />
                              <div>
                                <CharterFlightFareClassSelect />
                              </div>
                              <div>
                                <CharterFlightCalendarSelect
                                  selectedMonth={selectedMonth}
                                  onMonthChange={setSelectedMonth}
                                  nights={duration}
                                  fareClass={fareClass}
                                  onDurationChange={handleCharterDurationChange}
                                  durationOptions={CHARTER_BUNDLE_DURATION_OPTIONS}
                                  checkInDate={checkInDate}
                                  charterBundleRoute={charterBundleRoute}
                                  rooms={rooms}
                                />
                              </div>
                            </>}
                            {!charterBundleRoute && <StyledOfferCalendarDatePickerSelect
                              boundaryRef={desktopFiltersWrapperRef}
                            />}
                            {rooms.length > 1 && !charterBundleRoute && <OfferDesktopConnectingRoomsToggle />}
                            {flightsEnabled && !charterBundleRoute && <HotelDesktopFlightToggle />}
                            <Backdrop visible={!!activeFilter} data-testid="filter-backdrop" />
                          </FlexDesktopFilterBlock>
                        </CSSBreakpoint>
                      </WalledContent>}
                      {charterBundleRoute && <ReturnCharterFlightTile
                        route={charterBundleRoute}
                        occupancies={rooms}
                        checkInDate={checkInDate}
                        checkOutDate={checkOutDate}
                        fareClass={fareClass}
                        flightOrigin={airport}
                      />}
                      {!charterBundleRoute && <VerticalSpacer gap={8}>
                        <BodyText variant="large" align="center">
                          Select duration
                        </BodyText>
                        <OfferDurationTabs />
                      </VerticalSpacer>}
                      <VerticalSpacer gap={16}>
                        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
                          <div>
                            <Heading variant="heading3" as="h2">
                              Select your {rooms.length > 1 ? pluralizeToString(offer.saleUnit, rooms.length) : offer.saleUnit}
                            </Heading>
                            {canUseMultiRoomUI && <BodyText variant="medium">
                              Choose the same package for all rooms, or customise each room individually.
                            </BodyText>}
                          </div>
                          {!!charterBundleRoute && <PriceModeSelectDesktop />}
                        </Group>
                        {canUseMultiRoomUI && <Group direction="horizontal"><MultiRoomModeToggle inputsName="room-mode-desktop" /></Group>}
                      </VerticalSpacer>
                    </VerticalSpacer>
                  </LayoutContainer>
                  {!multipleRoomBookingActive && offer.packages.length > 0 && <FlashHotelDesktopSameRoomOptions
                    destinationAirport={destinationAirport}
                    onStickyFooterToggle={onStickyFooterToggle}
                  />}
                  {multipleRoomBookingActive && <FlashHotelDesktopIndividualRooms />}
                  {showFlightInformation && (
                    <LayoutContainer>
                      <Caption variant="medium" colour="neutral-two" align="center">
                        *Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {config.brandConfig.name} full Terms and Conditions apply.
                      </Caption>
                    </LayoutContainer>
                  )}
                </VerticalSpacer>
              </CSSBreakpoint>
            </VerticalSpacer>
          </PackageOptionsPane>
          {isMobileDevice && availableForPurchase && !(multipleRoomBookingActive && showNewMobileMultipleRoom) && <AccommodationOfferPageStickyFooter />}
          {!isMobileDevice && availableForPurchase && <HotelOfferPageStickyFooter
            visible={stickyFooterVisible}
            lowestPricePackage={offer.lowestPricePackage ?? offer.packages[0]}
            onScrollTo={scrollToPackageOptions}
            offer={offer}
          />}
        </SoftOfferPageWalledGarden>
      </RelativeBlock>
      <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    </ProductPaletteProvider>
  )
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'availableForPurchase'
  | 'bestPackage'
  | 'room'
  | 'activeHeaderFilter'
  | 'flightsEnabled'
  | 'isFlightsSelected'
  | 'roomMode'
  | 'multipleRoomBookingActive'
  | 'canUseMultiRoomUI'
  | 'charterBundleRoute'
  | 'fareClass'
  | 'priceMode'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  room: state.room,
  flightsEnabled: offerFlightsEnabled(state, appState),
  isFlightsSelected: state.flights,
  availableForPurchase: isAvailableForPurchase(state, appState),
  bestPackage: getCheapestSelectedPackageOptionWithDefault(appState, state),
  activeHeaderFilter: state.activeHeaderFilter,
  roomMode: state.multiRoomMode,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
  charterBundleRoute: state.charterBundleRoute,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
}), FlashHotelContent)
